import { api } from "api";
import { useQuery } from "@tanstack/react-query";

/**
 * Client hook to provide a P/RP id based on a PMS patient id, provisioning the P/RP if needed.
 * Also capable of provisioning insurance carriers, given an insurance carrier name.
 * If no identifiers are provided, the query will be disabled.
 * Not used to provision patients manually in non-integrated scenarios. For that, see the useProvisionPatientManually mutation.
 *
 * NOTE: THIS IS A WEIRD CASE. This is a mutation, but we want to perform it automatically, so we're pretending it is a
 * query. We can only get away with this because we have specifically designed the API to be idempotent, so it is safe
 * to trigger the mutation potentially many times! Additionally, we use a staleTime of Infinity to avoid subsequent
 * requests, and we have logic to use the existing value if we already have it.
 * @param pmsId PMS (VyneSync) identifier.
 * @param transactorId P/RP id or insurance carrier id, if already known. If supplied, query will return this value immediately.
 * @param insuranceCarrierName Name of the insurance carrier to provision. Ignored if pmsId is also provided.
 * @returns React Query properties.
 */
export function useProvisionTransactor(
    pmsId: string | undefined,
    transactorId: number | undefined,
    insuranceCarrierName?: string,
) {
    const enabled = !!pmsId || !!transactorId || !!insuranceCarrierName;

    return useQuery({
        queryKey: ["provisionTransactor", pmsId, transactorId, insuranceCarrierName],
        queryFn: async () => {
            if (transactorId) {
                return transactorId; // just return already known id. no need to actually provision
            }

            if (pmsId) {
                return await api.provider.provision(pmsId);
            } else if (insuranceCarrierName) {
                return await api.provider.provisionInsurancePayment({ name: insuranceCarrierName });
            } else {
                throw new Error("useProvisionTransactor called without pmsId or insuranceCarrierName");
            }
        },
        enabled,
        staleTime: Infinity,
        retry: 3,
    });
}
